/**
 * Agent.extraction_rules 편집기 (M13 / C-1).
 *
 * 두 가지 모드를 토글로 전환:
 *
 * 1) "폼 모드" (기본)
 *    - 알려진 invoice 필드 (12종) 중 선택 + 자유 입력
 *    - 필드별 hint 텍스트만 편집 — 가장 일반적인 케이스 99% 커버
 *    - 추가 / 제거 / 재정렬은 단순 row 조작
 *
 * 2) "JSON 모드"
 *    - 기존 raw JSON textarea
 *    - 비표준 스키마 (중첩, 신규 필드 등) 가 필요할 때
 *
 * 두 모드는 동일한 `rulesText` 문자열을 backing store 로 공유.
 * 모드 전환 시 parse/serialize 가 한 번 더 일어나며, 다른 한쪽이 invalid 상태이면
 * "고급 JSON 모드에 진입할 수 있도록" 폼 모드를 disable 한다.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { LLM_FIELD_META_MAP } from "@/features/records/fieldLabels";

import {
  KNOWN_INVOICE_FIELDS,
  parseRulesForForm,
  serializeEntriesToJson,
  type RulesFormEntry,
} from "./extraction_rules_helpers";

export type RulesMode = "form" | "json";

interface Props {
  mode: RulesMode;
  onModeChange: (mode: RulesMode) => void;
  rulesText: string;
  onRulesTextChange: (text: string) => void;
  /** AgentFormModal 의 parse 결과 (외부에서 동일하게 검증 필요할 때 활용). */
  parseError: string | null;
  disabled?: boolean;
}

export default function ExtractionRulesEditor({
  mode,
  onModeChange,
  rulesText,
  onRulesTextChange,
  parseError,
  disabled,
}: Props) {
  const { t } = useTranslation();
  const parsed = useMemo(() => parseRulesForForm(rulesText), [rulesText]);

  // ────────────── 폼 모드 로컬 상태 ──────────────
  //
  // 폼 행(entries)을 로컬 상태로 보유한다. rulesText 에서만 파생하면
  // ``serializeEntriesToJson`` 이 field/hint 가 빈 행을 즉시 제거하기 때문에
  // "규칙 추가"로 만든 빈 행이나 "직접 입력" 전환 중의 빈 field 행이 곧바로
  // 사라져 편집 자체가 불가능해진다. 로컬 상태가 편집 중인 미완성 행을 붙잡고,
  // 부모(rulesText)에는 유효한 행만 직렬화해 올려보낸다.
  const [entries, setEntries] = useState<RulesFormEntry[]>(
    () => parseRulesForForm(rulesText).entries,
  );
  // 우리가 마지막으로 부모에 밀어올린 직렬화 결과. 이 값과 동일한 rulesText 변경은
  // "우리 자신의 echo" 이므로 재-seed 하지 않는다(편집 중 빈 행 보호).
  const lastPushed = useRef<string>(serializeEntriesToJson(entries));

  useEffect(() => {
    if (rulesText.trim() === lastPushed.current.trim()) return; // 자기 echo 무시
    const p = parseRulesForForm(rulesText);
    if (!p.formCompatible) return; // JSON 전용 상태에선 폼 재구성 불가 → 유지
    setEntries(p.entries);
    lastPushed.current = rulesText;
  }, [rulesText]);

  // ────────────── 폼 모드 핸들러 (entries 변경 → rulesText 동기화) ──────────────

  const updateEntries = (next: RulesFormEntry[]) => {
    setEntries(next);
    const json = serializeEntriesToJson(next);
    lastPushed.current = json;
    onRulesTextChange(json);
  };

  const setField = (idx: number, field: string) => {
    updateEntries(entries.map((e, i) => (i === idx ? { ...e, field } : e)));
  };
  const setHint = (idx: number, hint: string) => {
    updateEntries(entries.map((e, i) => (i === idx ? { ...e, hint } : e)));
  };
  const removeEntry = (idx: number) => {
    updateEntries(entries.filter((_, i) => i !== idx));
  };
  const addEntry = () => {
    // 로컬 상태가 빈 hint 행을 붙잡으므로 곧바로 사라지지 않는다.
    // ``KNOWN_INVOICE_FIELDS`` 는 derive 된 string[] 이므로 noUncheckedIndexedAccess
    // 환경에서는 ``[0]`` 가 ``string | undefined`` 로 추론된다 → fallback 부여.
    const defaultField = KNOWN_INVOICE_FIELDS[0] ?? "billing_party";
    updateEntries([...entries, { field: defaultField, hint: "" }]);
  };

  // ────────────── 모드 토글 ──────────────

  const switchToForm = () => {
    if (!parsed.formCompatible) return; // 데이터 손실 방지
    onModeChange("form");
  };
  const switchToJson = () => onModeChange("json");

  return (
    <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50/50 p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-700">
          {t("admin.agents.form.rulesLabel")}
        </span>
        <div
          role="tablist"
          aria-label={t("admin.agents.form.rulesModeLabel")}
          className="inline-flex overflow-hidden rounded-md border border-slate-300 text-[11px]"
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === "form"}
            onClick={switchToForm}
            disabled={disabled || !parsed.formCompatible}
            title={!parsed.formCompatible ? t("admin.agents.form.rulesFormUnavailable") : undefined}
            className={
              mode === "form"
                ? "bg-blue-600 px-2.5 py-1 font-medium text-white"
                : "bg-white px-2.5 py-1 text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            }
          >
            {t("admin.agents.form.rulesModeForm")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "json"}
            onClick={switchToJson}
            disabled={disabled}
            className={
              mode === "json"
                ? "bg-blue-600 px-2.5 py-1 font-medium text-white"
                : "bg-white px-2.5 py-1 text-slate-600 transition hover:bg-slate-50"
            }
          >
            {t("admin.agents.form.rulesModeJson")}
          </button>
        </div>
      </div>

      {mode === "form" ? (
        <div className="space-y-2">
          {entries.length === 0 ? (
            <p className="text-[11px] italic text-slate-400">
              {t("admin.agents.form.rulesFormEmpty")}
            </p>
          ) : (
            entries.map((entry, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <select
                  value={
                    (KNOWN_INVOICE_FIELDS as readonly string[]).includes(entry.field)
                      ? entry.field
                      : "__custom__"
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    // "직접 입력" 선택 시: 현재 값이 알려진 필드면 빈 문자열로 비워
                    // 커스텀 입력칸(!KNOWN 조건)이 나타나게 한다. 이미 커스텀이면 유지.
                    setField(
                      idx,
                      v === "__custom__"
                        ? (KNOWN_INVOICE_FIELDS as readonly string[]).includes(entry.field)
                          ? ""
                          : entry.field
                        : v,
                    );
                  }}
                  disabled={disabled}
                  className="w-44 shrink-0 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs"
                >
                  {KNOWN_INVOICE_FIELDS.map((f) => {
                    const meta = LLM_FIELD_META_MAP[f];
                    const label = meta ? t(meta.labelI18nKey) : f;
                    return (
                      <option key={f} value={f}>
                        {meta ? `${label} — ${f}` : f}
                      </option>
                    );
                  })}
                  <option value="__custom__">{t("admin.agents.form.rulesCustomField")}</option>
                </select>
                {!(KNOWN_INVOICE_FIELDS as readonly string[]).includes(entry.field) ? (
                  <input
                    type="text"
                    value={entry.field}
                    onChange={(e) => setField(idx, e.target.value)}
                    placeholder={t("admin.agents.form.rulesFieldPlaceholder")}
                    disabled={disabled}
                    className="w-40 shrink-0 rounded-md border border-slate-300 px-2 py-1 font-mono text-xs"
                  />
                ) : null}
                <input
                  type="text"
                  value={entry.hint}
                  onChange={(e) => setHint(idx, e.target.value)}
                  placeholder={t("admin.agents.form.rulesHintPlaceholder")}
                  disabled={disabled}
                  className="flex-1 rounded-md border border-slate-300 px-2 py-1 text-xs"
                />
                <button
                  type="button"
                  onClick={() => removeEntry(idx)}
                  disabled={disabled}
                  className="rounded-md p-1 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                  aria-label={t("admin.agents.form.rulesRemoveRow", { field: entry.field })}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))
          )}
          <button
            type="button"
            onClick={addEntry}
            disabled={disabled}
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-slate-300 px-2 py-1 text-[11px] text-slate-600 transition hover:bg-white"
          >
            <Plus className="h-3 w-3" />
            {t("admin.agents.form.rulesAddRow")}
          </button>
        </div>
      ) : (
        <textarea
          rows={5}
          value={rulesText}
          onChange={(e) => onRulesTextChange(e.target.value)}
          placeholder='{"invoice_number": {"hint": "Extract after N°"}}'
          className="w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          disabled={disabled}
        />
      )}

      {parseError ? (
        <p className="text-[11px] text-red-600">{parseError}</p>
      ) : (
        <p className="text-[11px] text-slate-400">{t("admin.agents.form.rulesHint")}</p>
      )}
    </div>
  );
}
